// ==================== RESELLER FORM MODAL ====================
// Super admin: create a reseller login, edit its details, or set a new password.
// No email is ever sent; the super admin shares the login with the reseller.

import { useState } from 'react';
import { Building2, Lock, Mail, Phone, User } from 'lucide-react';
import { Button, Input, Modal } from '../ui';
import { resellersService, type Reseller } from '../../services/resellers.service';

// Same rule as the API (REGEX.PASSWORD, 8–50 characters)
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,50}$/;

interface ResellerFormModalProps {
  reseller?: Reseller | null;
  mode?: 'create' | 'edit' | 'password';
  onClose: () => void;
  onSaved: () => void;
}

export const ResellerFormModal = ({ reseller, mode = 'create', onClose, onSaved }: ResellerFormModalProps) => {
  const [firstName, setFirstName] = useState(reseller?.firstName ?? '');
  const [lastName, setLastName] = useState(reseller?.lastName ?? '');
  const [email, setEmail] = useState(reseller?.email ?? '');
  const [phoneNumber, setPhoneNumber] = useState(reseller?.phoneNumber ?? '');
  const [companyName, setCompanyName] = useState(reseller?.companyName ?? '');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (mode !== 'edit' && !PASSWORD_PATTERN.test(password)) {
      setError('Password must be 8–50 characters with uppercase, lowercase, a number and a special character.');
      return;
    }
    if (mode !== 'edit' && password !== confirmation) {
      setError('Passwords do not match');
      return;
    }
    setSaving(true);
    setError(null);
    const details = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneNumber: phoneNumber.trim(),
      companyName: companyName.trim(),
    };
    const response =
      mode === 'password' && reseller
        ? await resellersService.resetPassword(reseller.id, password)
        : mode === 'edit' && reseller
          ? await resellersService.update(reseller.id, details)
          : await resellersService.create({ ...details, email: email.trim().toLowerCase(), password });
    setSaving(false);
    if (response.success) onSaved();
    else setError(response.message);
  };

  return (
    <Modal
      isOpen
      onClose={() => !saving && onClose()}
      title={mode === 'password' ? 'Reset Password' : mode === 'edit' ? 'Edit Reseller' : 'Create Reseller'}
      subtitle={reseller?.email}
      size="lg"
      contentClassName="p-6"
    >
      <form onSubmit={(event) => void save(event)} className="space-y-4">
        {mode !== 'password' && (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="First Name"
                placeholder="e.g., Ali"
                leftIcon={<User className="w-4 h-4" />}
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                required
                minLength={2}
                maxLength={50}
                disabled={saving}
              />
              <Input
                label="Last Name"
                placeholder="e.g., Khan"
                leftIcon={<User className="w-4 h-4" />}
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                required
                minLength={2}
                maxLength={50}
                disabled={saving}
              />
            </div>
            <Input
              label="Email Address"
              type="email"
              placeholder="e.g., partner@company.com"
              leftIcon={<Mail className="w-4 h-4" />}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              disabled={saving || mode === 'edit'}
              hint={mode === 'edit' ? 'The login email cannot be changed.' : 'Used to sign in. No email is sent to it.'}
            />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="Contact Number"
                type="tel"
                placeholder="+1 (555) 123-4567"
                leftIcon={<Phone className="w-4 h-4" />}
                minLength={10}
                maxLength={20}
                pattern={String.raw`[0-9+\-\s\(\)]+`}
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                required
                disabled={saving}
              />
              <Input
                label="Company"
                placeholder="e.g., Acme Marketing"
                leftIcon={<Building2 className="w-4 h-4" />}
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                required
                minLength={1}
                maxLength={255}
                disabled={saving}
              />
            </div>
          </>
        )}
        {mode !== 'edit' && (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label={mode === 'password' ? 'New Password' : 'Password'}
                type="password"
                placeholder="Enter password"
                leftIcon={<Lock className="w-4 h-4" />}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={8}
                maxLength={50}
                showPasswordToggle
                disabled={saving}
              />
              <Input
                label="Confirm Password"
                type="password"
                placeholder="Confirm password"
                leftIcon={<Lock className="w-4 h-4" />}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                required
                minLength={8}
                maxLength={50}
                showPasswordToggle
                disabled={saving}
              />
            </div>
            <p className="text-xs text-(--color-muted-gray)">
              At least 8 characters with uppercase, lowercase, a number and a special character.
            </p>
          </>
        )}
        {error && (
          <p role="alert" className="text-sm text-(--color-error)">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-4 border-t border-(--color-border-gray)">
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={saving}>
            {mode === 'password' ? 'Update Password' : mode === 'edit' ? 'Save Changes' : 'Create Reseller'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
